import { selectedFontPayload } from '../../../../interfaces/chat';
import { appState } from '../../../../interfaces/common';
import { Button, Card, Flex, Icon, RadioButton, RadioButtonGroup, Text } from '@pega/cosmos-react-core';
import { ColumnProps } from '@pega/cosmos-react-core/lib/components/Table/Table';
import React, { ChangeEvent, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import useLocalTranslation from '../../../../hooks/useLocalTranslation';
import { setChatInfo, updateBannerInfo, updateLoaderDetails } from '../../../../redux/actions';
import { InteractiveText, StyledTableNoPadding } from '../../../Utilities/custom.styled';
import { deletefontRecord, getFontFileMetaData, updateSelectedFontFile, validateUploadFontFile } from '../ChatApis';

const UploadFontFile: React.FC = () => {
  const fileRef = useRef<any>(null);
  const translate = useLocalTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    chatInfo: { connectionId },
    sessionId,
  } = useSelector((state: appState) => {
    return {
      chatInfo: state.chatInfo,
      sessionId: state.sessionId,
    };
  });

  const { fontSet = [], selectedFontId } = useSelector((state: appState) => state.chatInfo);

  const validateFile = async (requestId: string, fileNameWithoutExtension: string) => {
    let response;
    try {
      response = await validateUploadFontFile(sessionId, connectionId, requestId, fileNameWithoutExtension);
    } catch (error) {
      dispatch(updateBannerInfo(translate('file_upload_failed'), 'urgent'));
    }
    dispatch(updateLoaderDetails(false));
    if (response?.status === 200) {
      dispatch(setChatInfo(response.data));
      dispatch(updateBannerInfo(translate('file_upload_success'), 'success'));
    } else if (response?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate('file_upload_failed'), 'urgent'));
    }
  };

  const uploadFile = async (urlAndFields: any, file: File, fileNameWithoutExtension: string) => {
    const { url, fields } = urlAndFields;
    const requestId = fields['x-amz-meta-request-id'];
    const formData = new FormData();
    Object.keys(fields).forEach((key) => {
      formData.append(key, fields[key]);
    });
    formData.append('file', file);
    await fetch(url, {
      method: 'POST',
      body: formData,
    })
      .then(() => {
        if (requestId) {
          validateFile(requestId, fileNameWithoutExtension);
        }
      })
      .catch(() => {
        dispatch(updateLoaderDetails(false));
        dispatch(updateBannerInfo(translate('file_upload_failed'), 'urgent'));
      });
  };

  const getUploadUrl = async (file: File) => {
    const fileName = file.name;
    const fileNameWithoutExtension = fileName.split('.')[0];
    dispatch(updateLoaderDetails(true));
    let response = await getFontFileMetaData(sessionId, connectionId, fileNameWithoutExtension);
    if (response?.status === 200) {
      uploadFile(response.data, file, fileNameWithoutExtension);
    } else if (response?.status === 403) {
      dispatch(updateLoaderDetails(false));
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateLoaderDetails(false));
      dispatch(updateBannerInfo(translate('file_upload_failed'), 'urgent'));
    }
  };

  const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.currentTarget.files && event.currentTarget.files.length > 0) {
      const file = event.currentTarget.files[0];
      const fileType = file?.name?.split('.')?.pop() as string;
      if (['woff', 'woff2', 'otf', 'ttf'].includes(fileType) && fontSet.length < 5) {
        getUploadUrl(file);
      } else {
        dispatch(updateBannerInfo(translate(fontSet.length < 5 ? 'font_file_criteria' : 'font_file_limit'), 'urgent'));
      }
    }
  };

  const deleteTagRecord = async (fontId: string) => {
    const payload = { fontId: fontId };
    dispatch(updateLoaderDetails(true));
    const json = await deletefontRecord(sessionId, connectionId, payload);
    dispatch(updateLoaderDetails(false));
    if (json?.status === 200) {
      dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(translate('font_deleted'), 'success'));
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate('font_delete_failed'), 'urgent'));
    }
  };

  const displayFilename = (fontId: string) => {
    let fontName = fontId ? fontId.split('/').pop() : '';
    return fontName;
  };

  const updateSelectedFont = async (e: ChangeEvent<HTMLInputElement>) => {
    const fontFileId = e.currentTarget.value;
    let payload: selectedFontPayload = {
      selectedFontId: fontFileId,
    };
    dispatch(updateLoaderDetails(true));
    let json = await updateSelectedFontFile(sessionId, connectionId, payload);
    if (json?.status === 200) {
      dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(translate('font_update_success'), 'success'));
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate('font_update_failed'), 'urgent'));
    }
    dispatch(updateLoaderDetails(false));
  };

  const data = fontSet.map((item, index) => {
    return {
      id: index,
      tag: (
        <Flex
          container={{ direction: 'row', justify: 'between', alignItems: 'center' }}
          style={{
            backgroundColor: fontSet.length > 1 && item.fontId === selectedFontId ? '#e9eef3' : '#ffff',
            padding: '0.5rem 1rem',
          }}
        >
          {fontSet.length === 1 ? (
            <Text>{displayFilename(item.fontId)}</Text>
          ) : (
            <RadioButtonGroup name="fontFile" onChange={updateSelectedFont}>
              <RadioButton
                label={displayFilename(item.fontId)}
                checked={item.fontId === selectedFontId}
                value={item.fontId}
              />
            </RadioButtonGroup>
          )}
          <Button variant="simple" icon onClick={() => deleteTagRecord(item.fontId)}>
            <Icon name="trash" />
          </Button>
        </Flex>
      ),
    };
  });

  const columns: ColumnProps<(typeof data)[number]>[] = [{ renderer: 'tag', label: translate('font_name') }];

  return (
    <Flex
      container={{
        direction: 'column',
        itemGap: 2,
      }}
      style={{ paddingTop: '12px' }}
    >
      <Text variant="h3" as="h3">
        {translate('widget_font')}
      </Text>
      <Flex container={{ direction: 'column' }}>
        <Text>
          <InteractiveText
            className="pointer"
            onClick={() => {
              fileRef?.current.click();
            }}
          >
            {translate('upload_font')}
          </InteractiveText>
        </Text>

        <input
          type="file"
          accept=".ttf,.otf,.woff,.woff2"
          ref={fileRef}
          onChange={onFileChange}
          style={{ display: 'none' }}
        />
        <Text variant="secondary">{translate('font_file_criteria')}</Text>
      </Flex>
      {fontSet.length > 0 && (
        <Card style={{ padding: 0 }}>
          <StyledTableNoPadding
            hoverHighlight={true}
            padding={'default'}
            columns={columns}
            data={data}
            emptyContent={
              <Text variant="h5" as="h5" style={{ padding: '0.5rem 0' }}>
                {translate('no_tags_configured')}
              </Text>
            }
            style={{ width: '85%' }}
          />
        </Card>
      )}
      <Flex>
        <Text>
          <b>{translate('note')}:</b> {translate('widget_font_note')}
        </Text>
      </Flex>
    </Flex>
  );
};

export default UploadFontFile;
